/**
 * Generic substring-based URL swap that walks a data tree.
 *
 * Used by the US-locale override files to produce a locale-specific
 * copy of India data with a small set of image paths replaced. The
 * walker only touches string leaves, so non-string values (including
 * FontAwesome IconDefinition objects, numbers, booleans) pass through
 * unchanged.
 *
 * Returns the same reference when nothing changed, so untouched
 * subtrees share memory with the source object and Next.js can still
 * serialize them via getStaticProps (no accidental `undefined` where
 * a key was previously omitted).
 */
export type ImageSwapMap = Record<string, string>;

const swapString = (value: string, swaps: ImageSwapMap): string => {
  for (const [from, to] of Object.entries(swaps)) {
    if (value.includes(from)) return value.replace(from, to);
  }
  return value;
};

export const deepSwapImageUrls = <T>(value: T, swaps: ImageSwapMap): T => {
  if (typeof value === "string") {
    const swapped = swapString(value, swaps);
    return swapped === value ? value : (swapped as unknown as T);
  }
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((item) => {
      const n = deepSwapImageUrls(item, swaps);
      if (n !== item) changed = true;
      return n;
    });
    return changed ? (next as unknown as T) : value;
  }
  if (value && typeof value === "object") {
    // Skip non-plain objects. FontAwesome IconDefinition instances are
    // plain objects in practice, but their `icon` tuple contains
    // numbers/strings only — safe to walk. Guard against Class instances
    // or exotic objects by sticking to Object.prototype descendants.
    const proto = Object.getPrototypeOf(value);
    if (proto !== null && proto !== Object.prototype) return value;

    let changed = false;
    const next: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const n = deepSwapImageUrls(v, swaps);
      if (n !== v) changed = true;
      next[k] = n;
    }
    return changed ? (next as unknown as T) : value;
  }
  return value;
};
